import { NavLink, useLocation } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { hasRole } from '@/auth/types';
import { NAV_FOOTER_ITEMS, NAV_GROUPS, visibleNavGroups, type NavItem } from '@/navigation';
import { cx } from '@/lib/cx';

const ALL_NAV_PATHS = [...NAV_GROUPS.flatMap((g) => g.items), ...NAV_FOOTER_ITEMS].map((i) => i.path);

/** 現在のパスに、より長く一致するナビ項目があればその項目を優先する（例: vessels/plan-days 表示中は vessels を強調しない） */
function hasMoreSpecificMatch(itemPath: string, rest: string): boolean {
  return ALL_NAV_PATHS.some(
    (p) => p !== itemPath && p.startsWith(`${itemPath}/`) && (rest === p || rest.startsWith(`${p}/`)),
  );
}

function Item({ item, to }: { item: NavItem; to: string }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const rest = pathname.replace(/^\/years\/[^/]+\/?/, '');
  const shadowed = hasMoreSpecificMatch(item.path, rest);
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cx('bs-nav__link', isActive && !shadowed && 'is-active', item.status === 'planned' && 'is-planned')
        }
      >
        <Icon size={16} strokeWidth={1.8} aria-hidden className="bs-nav__icon" />
        <span className="bs-nav__label">{item.label}</span>
        {item.status === 'planned' && <span className="bs-nav__planned" title="準備中" aria-label="準備中" />}
      </NavLink>
    </li>
  );
}

/** 機能グループ別サイドナビ。表示項目は navigation.ts とロールで決まる */
export function SideNav() {
  const user = useCurrentUser();
  const { yearPath } = useFiscalYear();
  const groups = visibleNavGroups(user);

  return (
    <nav className="bs-nav" aria-label="メインメニュー">
      <ul className="bs-nav__top">
        <li>
          <NavLink end to={yearPath()} className={({ isActive }) => cx('bs-nav__link bs-nav__link--home', isActive && 'is-active')}>
            <LayoutGrid size={16} strokeWidth={1.8} aria-hidden className="bs-nav__icon" />
            <span className="bs-nav__label">ポータル</span>
          </NavLink>
        </li>
      </ul>
      {groups.map((g) => (
        <section key={g.id} className="bs-nav__group" aria-labelledby={`nav-${g.id}`}>
          <h2 id={`nav-${g.id}`} className="bs-nav__heading">
            <span className="bs-nav__code">{g.code}</span>
            {g.label}
          </h2>
          <ul>
            {g.items.map((i) => (
              <Item key={i.id} item={i} to={yearPath(i.path)} />
            ))}
          </ul>
        </section>
      ))}
      <ul className="bs-nav__footer">
        {NAV_FOOTER_ITEMS.filter((i) => hasRole(user, i.roles)).map((i) => (
          <Item key={i.id} item={i} to={yearPath(i.path)} />
        ))}
      </ul>
      <p className="bs-nav__legend">
        <span className="bs-nav__planned" aria-hidden /> 準備中（順次公開）
      </p>
    </nav>
  );
}
